import type {
  Category,
  Channel,
  ChannelPlacement,
  ChannelType,
  GuildBan,
  Message,
  PublicUser,
  Role,
  UpdateChannelInput,
} from '@discordo/shared';
import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

import { ApiError, apiFetch, apiUpload } from '../lib/api';
import { createSocket, type DiscordoSocket } from '../lib/socket';
import { useAuth } from './auth';

/**
 * State of the app shell: the guild, its channels and members, the open
 * channel and its messages.
 *
 * Two sources feed it and they must not fight. REST answers "what is there"
 * when a channel opens or history is pulled; the socket answers "what changed"
 * from then on. Everything they produce goes through the same upsert keyed by
 * message id, so a message that arrives twice — once as the reply to the POST
 * that created it and once as the broadcast the sender also receives — lands
 * in the list exactly once.
 *
 * A person belongs to any number of servers and reads one at a time. Which one
 * is the only thing this store keeps outside React — in `localStorage` — so a
 * reload lands where the last session left off. Everything else about a server
 * (its name, its icon, its channels, its members) is data read at runtime from
 * the same routes for every server: there is no per-server code here, and
 * none anywhere else either (§3 of the Fase 4 design).
 *
 * Switching servers is therefore not a second code path — it is the same code
 * reading another row. What it does have to do is *forget*: channels, members,
 * history, presence and voice all belong to the server that was open, and the
 * socket rooms most of all. A room nobody left keeps delivering messages from
 * a server that is no longer on screen, which is a leak between servers rather
 * than a stale view.
 */

// ---------------------------------------------------------------------------
// Wire shapes
// ---------------------------------------------------------------------------

/** `GET /api/guilds` */
export interface GuildSummary {
  id: string;
  name: string;
  iconUrl: string | null;
  ownerId: string;
  /** Role of the signed-in user in this guild. */
  role: Role;
}

/** `GET /api/guilds/:id/members` */
export interface GuildMemberSummary {
  user: PublicUser;
  nickname: string | null;
  role: Role;
}

/** What the sidebar's creation dialog collected. */
export interface CreateChannelDraft {
  name: string;
  type: ChannelType;
  categoryId: string | null;
}

/** `GET /api/channels/:id/messages` */
interface MessagePage {
  messages: Message[];
  nextCursor: string | null;
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

export interface ChannelsContextValue {
  /** Every server the signed-in person belongs to, in the API's order. */
  guilds: GuildSummary[];
  /** The one being read; `null` only while the first list is still loading. */
  guild: GuildSummary | null;
  selectGuild: (guildId: string) => void;
  /**
   * Opens a server and switches to it. The API creates its default channels,
   * by the same function the seed uses.
   */
  createGuild: (name: string) => Promise<void>;
  channels: Channel[];
  /** Groups of channels of the open server, in the order the sidebar shows. */
  categories: Category[];
  members: GuildMemberSummary[];
  activeChannel: Channel | null;
  selectChannel: (channelId: string) => void;
  /**
   * Opens a channel in the current server. The API refuses anyone who is not
   * an administrator of it, which is the rule the sidebar also reads to decide
   * whether to offer the button at all.
   */
  createChannel: (input: CreateChannelDraft) => Promise<void>;
  /** Renames the channel, sets its topic, or moves it between categories. */
  updateChannel: (channelId: string, patch: UpdateChannelInput) => Promise<void>;
  /** Removes the channel. Its messages go with it, by cascade in the database. */
  deleteChannel: (channelId: string) => Promise<void>;
  createCategory: (name: string) => Promise<void>;
  updateCategory: (categoryId: string, name: string) => Promise<void>;
  /** Removes the group. The channels that were in it stay, ungrouped. */
  deleteCategory: (categoryId: string) => Promise<void>;
  /**
   * Writes the whole arrangement of the sidebar at once — which group each
   * channel is in and the order they are read in.
   */
  reorderChannels: (order: ChannelPlacement[]) => Promise<void>;
  /**
   * Removes somebody from the open server. They may come back through any
   * invite still open — which is what `banMember` is for.
   */
  kickMember: (userId: string) => Promise<void>;
  /** Removes somebody and records that they may not come back. */
  banMember: (userId: string, reason?: string) => Promise<void>;
  /**
   * Who is barred from the open server.
   *
   * It answers instead of storing: the ban screen is opened rarely and read
   * once, and keeping the list in the shell would be one more thing to
   * invalidate on every ban, every revoke and every change of server.
   */
  listBans: () => Promise<GuildBan[]>;
  /** Lifts a ban. It does not put the person back in the server. */
  revokeBan: (userId: string) => Promise<void>;
  messages: Message[];
  /** True while older pages remain to be pulled for the open channel. */
  hasMore: boolean;
  /** True until the guild, its channels and its members have loaded. */
  loading: boolean;
  loadingMessages: boolean;
  loadingOlder: boolean;
  loadOlder: () => void;
  sendMessage: (content: string) => Promise<void>;
  /**
   * Sends one file to the open channel, reporting how much of it has left the
   * browser. The message the API answers with carries the attachment and lands
   * in the history like any other.
   */
  sendAttachment: (file: File, onProgress?: (fraction: number) => void) => Promise<void>;
  /** Announces to the channel that this user is typing, with no throttling. */
  notifyTyping: () => void;
  typingUserIds: string[];
  onlineUserIds: ReadonlySet<string>;
  /**
   * Who is connected to each voice channel, keyed by channel id, as the SFU
   * last reported it. A channel no event has spoken about yet is absent; one
   * everybody left maps to an empty list.
   */
  voiceParticipants: ReadonlyMap<string, PublicUser[]>;
  error: string | null;
}

const ChannelsContext = createContext<ChannelsContextValue | null>(null);

const PAGE_SIZE = 50;

/** How long a typing indicator survives without a new frame from its author. */
const TYPING_TIMEOUT_MS = 5_000;

const GENERIC_FAILURE = 'Não foi possível carregar o servidor. Tente recarregar a página.';

/** Where the last server read is remembered, so a reload lands back in it. */
const ACTIVE_GUILD_STORAGE_KEY = 'discordo:guild';

/**
 * Both directions go through try/catch. A private window may refuse storage
 * outright, and forgetting which server was open must never be what keeps the
 * app from opening: without an answer the first server of the list is used,
 * which is the default anyway.
 */
export function readRememberedGuild(): string | null {
  try {
    return window.localStorage.getItem(ACTIVE_GUILD_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function rememberGuild(guildId: string): void {
  try {
    window.localStorage.setItem(ACTIVE_GUILD_STORAGE_KEY, guildId);
  } catch {
    // Nothing to recover: this session still works, the next one just starts
    // on the first server of the list.
  }
}

function describe(cause: unknown): string {
  return cause instanceof ApiError ? cause.message : GENERIC_FAILURE;
}

/** Adds the message, or replaces the copy already on screen with the same id. */
function upsert(messages: Message[], message: Message): Message[] {
  const index = messages.findIndex((item) => item.id === message.id);
  if (index === -1) {
    return [...messages, message];
  }

  const next = [...messages];
  next[index] = message;
  return next;
}

export interface ChannelsProviderProps {
  children: ReactNode;
}

export function ChannelsProvider({ children }: ChannelsProviderProps): ReactElement {
  const { user } = useAuth();

  const [socket, setSocket] = useState<DiscordoSocket | null>(null);
  const [guilds, setGuilds] = useState<GuildSummary[]>([]);
  const [activeGuildId, setActiveGuildId] = useState<string | null>(null);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [members, setMembers] = useState<GuildMemberSummary[]>([]);
  const [activeChannelId, setActiveChannelId] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [typingUserIds, setTypingUserIds] = useState<string[]>([]);
  const [presentUserIds, setPresentUserIds] = useState<ReadonlySet<string>>(() => new Set());
  const [voiceParticipants, setVoiceParticipants] = useState<ReadonlyMap<string, PublicUser[]>>(
    () => new Map(),
  );
  const [error, setError] = useState<string | null>(null);

  /**
   * The connection is opened by the effect that owns it and closed by the same
   * effect's cleanup, which is the only arrangement that survives a mount →
   * unmount → mount cycle: every mount gets its own live socket, and the one a
   * cleanup killed is never the one the next mount holds. On logout the shell
   * unmounts and the connection goes with it, instead of outliving the cookie
   * that authenticated it.
   */
  useEffect(() => {
    const connection = createSocket();
    setSocket(connection);

    return () => {
      connection.disconnect();
    };
  }, []);

  // -------------------------------------------------------------------------
  // Loading
  // -------------------------------------------------------------------------

  /**
   * The list of servers, once. Which one opens is the one the last session
   * left off in, when it is still one of them — a server left or deleted
   * meanwhile falls back to the first, instead of to an empty screen.
   */
  useEffect(() => {
    let active = true;

    apiFetch<{ guilds: GuildSummary[] }>('/api/guilds')
      .then((page) => {
        if (!active) {
          return;
        }

        setGuilds(page.guilds);

        const remembered = readRememberedGuild();
        const chosen =
          page.guilds.find((item) => item.id === remembered) ?? page.guilds[0] ?? null;

        if (!chosen) {
          setError('Você ainda não faz parte de nenhum servidor.');
          setLoading(false);
          return;
        }

        setActiveGuildId(chosen.id);
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(describe(cause));
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, []);

  /**
   * Everything that belongs to the open server, reloaded whenever it changes.
   *
   * The clearing at the top is the whole point: what was on screen belongs to
   * the server being left, and leaving any of it behind — a member list, a
   * voice roster, who was online — would show one server's people inside
   * another. The socket rooms are dropped by the subscription effect below,
   * which is the other half of the same idea.
   */
  useEffect(() => {
    if (activeGuildId === null) {
      return undefined;
    }

    let active = true;

    setChannels([]);
    setCategories([]);
    setMembers([]);
    setActiveChannelId(null);
    setMessages([]);
    setNextCursor(null);
    setTypingUserIds([]);
    setPresentUserIds(new Set());
    setVoiceParticipants(new Map());

    Promise.all([
      apiFetch<{ channels: Channel[] }>(`/api/guilds/${activeGuildId}/channels`),
      apiFetch<{ members: GuildMemberSummary[] }>(`/api/guilds/${activeGuildId}/members`),
      apiFetch<{ categories: Category[] }>(`/api/guilds/${activeGuildId}/categories`),
    ])
      .then(([channelPage, memberPage, categoryPage]) => {
        if (!active) {
          return;
        }

        setChannels(channelPage.channels);
        setMembers(memberPage.members);
        setCategories(categoryPage.categories);
        setActiveChannelId(channelPage.channels.find((item) => item.type === 'text')?.id ?? null);
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(describe(cause));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [activeGuildId]);

  useEffect(() => {
    if (activeChannelId === null) {
      return undefined;
    }

    let active = true;
    setMessages([]);
    setNextCursor(null);
    setTypingUserIds([]);
    setLoadingMessages(true);

    apiFetch<MessagePage>(`/api/channels/${activeChannelId}/messages?limit=${PAGE_SIZE}`)
      .then((page) => {
        if (!active) {
          return;
        }
        setMessages(page.messages);
        setNextCursor(page.nextCursor);
      })
      .catch((cause: unknown) => {
        if (active) {
          setError(describe(cause));
        }
      })
      .finally(() => {
        if (active) {
          setLoadingMessages(false);
        }
      });

    return () => {
      active = false;
    };
  }, [activeChannelId]);

  // -------------------------------------------------------------------------
  // Realtime
  // -------------------------------------------------------------------------

  /**
   * Every voice channel of the guild, subscribed to whether or not this session
   * is connected to any of them: `voice:state` travels in the channel room, and
   * seeing who is in a room you have not joined is the point of showing it in
   * the sidebar.
   */
  const voiceChannelIds = useMemo(
    () => channels.filter((channel) => channel.type === 'voice').map((channel) => channel.id),
    [channels],
  );

  /**
   * Subscribing is what grants this socket the rooms it listens to, and it has
   * to happen again after every reconnection: the server keeps no memory of
   * the rooms a dropped socket used to be in.
   *
   * The cleanup leaves them again, which is what keeps a server's traffic from
   * outliving the moment it was on screen. The socket is shared by every
   * server — it is opened once, for the person, not for the server — so
   * without the leave, switching servers would keep the old rooms attached and
   * their messages, typing frames and voice state would go on arriving here.
   */
  useEffect(() => {
    if (socket === null) {
      return undefined;
    }

    const rooms = activeChannelId === null ? voiceChannelIds : [activeChannelId, ...voiceChannelIds];
    if (rooms.length === 0) {
      return undefined;
    }

    const subscribe = (): void => {
      for (const channelId of rooms) {
        socket.emit('channel:subscribe', { channelId }, () => undefined);
      }
    };

    if (socket.connected) {
      subscribe();
    }
    socket.on('connect', subscribe);

    return () => {
      socket.off('connect', subscribe);
      for (const channelId of rooms) {
        socket.emit('channel:unsubscribe', { channelId });
      }
    };
  }, [socket, activeChannelId, voiceChannelIds]);

  /**
   * Pulls the member list again.
   *
   * Nothing announces that someone joined the guild, so a session that loaded
   * the list before a new member signed up would keep an outdated list until a
   * reload. Their first `presence:update` is the only sign the list aged, and
   * this is the answer to it. A failed refresh is left alone on purpose: the
   * list already on screen stays, and the next presence event tries again.
   */
  const refreshMembers = useCallback(() => {
    if (activeGuildId === null) {
      return;
    }

    apiFetch<{ members: GuildMemberSummary[] }>(`/api/guilds/${activeGuildId}/members`)
      .then((page) => {
        setMembers(page.members);
      })
      .catch(() => undefined);
  }, [activeGuildId]);

  const typingTimers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  useEffect(() => {
    const timers = typingTimers.current;
    return () => {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();
    };
  }, []);

  const markTyping = useCallback((userId: string) => {
    const timers = typingTimers.current;
    const running = timers.get(userId);
    if (running) {
      clearTimeout(running);
    }

    timers.set(
      userId,
      setTimeout(() => {
        timers.delete(userId);
        setTypingUserIds((previous) => previous.filter((id) => id !== userId));
      }, TYPING_TIMEOUT_MS),
    );

    setTypingUserIds((previous) => (previous.includes(userId) ? previous : [...previous, userId]));
  }, []);

  useEffect(() => {
    if (socket === null) {
      return undefined;
    }

    const onNew = (message: Message): void => {
      if (message.channelId !== activeChannelId) {
        return;
      }
      setMessages((previous) => upsert(previous, message));
    };

    const onEdit = (payload: {
      id: string;
      channelId: string;
      content: string;
      editedAt: string;
    }): void => {
      if (payload.channelId !== activeChannelId) {
        return;
      }
      setMessages((previous) =>
        previous.map((message) =>
          message.id === payload.id
            ? { ...message, content: payload.content, editedAt: payload.editedAt }
            : message,
        ),
      );
    };

    const onDelete = (payload: { id: string; channelId: string }): void => {
      if (payload.channelId !== activeChannelId) {
        return;
      }
      setMessages((previous) => previous.filter((message) => message.id !== payload.id));
    };

    const onPresence = (payload: { userId: string; status: 'online' | 'offline' }): void => {
      setPresentUserIds((previous) => {
        const next = new Set(previous);
        if (payload.status === 'online') {
          next.add(payload.userId);
        } else {
          next.delete(payload.userId);
        }
        return next;
      });

      if (
        payload.status === 'online' &&
        !members.some((member) => member.user.id === payload.userId)
      ) {
        refreshMembers();
      }
    };

    const onVoiceState = (payload: { channelId: string; participants: PublicUser[] }): void => {
      setVoiceParticipants((previous) => {
        const next = new Map(previous);
        next.set(payload.channelId, payload.participants);
        return next;
      });
    };

    const onTyping = (payload: { channelId: string; userId: string }): void => {
      if (payload.channelId !== activeChannelId) {
        return;
      }
      markTyping(payload.userId);
    };

    socket.on('message:new', onNew);
    socket.on('message:edit', onEdit);
    socket.on('message:delete', onDelete);
    socket.on('presence:update', onPresence);
    socket.on('typing:start', onTyping);
    socket.on('voice:state', onVoiceState);

    return () => {
      socket.off('message:new', onNew);
      socket.off('message:edit', onEdit);
      socket.off('message:delete', onDelete);
      socket.off('presence:update', onPresence);
      socket.off('typing:start', onTyping);
      socket.off('voice:state', onVoiceState);
    };
  }, [socket, activeChannelId, markTyping, members, refreshMembers]);

  // -------------------------------------------------------------------------
  // Actions
  // -------------------------------------------------------------------------

  const selectChannel = useCallback((channelId: string) => {
    setError(null);
    setActiveChannelId(channelId);
  }, []);

  /**
   * Opens another server. The choice is written down before the state changes
   * so a reload during the switch still lands where the person was going.
   */
  const selectGuild = useCallback(
    (guildId: string) => {
      if (guildId === activeGuildId) {
        return;
      }

      setError(null);
      rememberGuild(guildId);
      setActiveGuildId(guildId);
    },
    [activeGuildId],
  );

  /**
   * Creates a server and switches to it.
   *
   * The API answers with the server as a member reads it — role included — so
   * the bar can show it without a second round trip, and its default channels
   * arrive on the load the switch triggers.
   */
  const createGuild = useCallback(async (name: string) => {
    const { guild: created } = await apiFetch<{ guild: GuildSummary }>('/api/guilds', {
      method: 'POST',
      body: { name },
    });

    setGuilds((previous) =>
      previous.some((item) => item.id === created.id) ? previous : [...previous, created],
    );
    setError(null);
    rememberGuild(created.id);
    setActiveGuildId(created.id);
  }, []);

  /**
   * Everything below writes to the API first and only then to the screen, and
   * what lands on the screen is the row the API answered with — never the one
   * the browser guessed. A channel has an id and a position that only the
   * server knows, and an optimistic copy of it would be a different channel
   * wearing the same name until the next reload.
   *
   * None of them checks permission: the API is what refuses, and the refusal
   * arrives as an `ApiError` the dialog shows. The sidebar hides these
   * controls from ordinary members so nobody is invited into a refusal, but
   * hiding a button is never what enforces the rule.
   */
  const createChannel = useCallback(
    async (input: CreateChannelDraft) => {
      if (activeGuildId === null) {
        return;
      }

      const { channel } = await apiFetch<{ channel: Channel }>('/api/channels', {
        method: 'POST',
        body: { ...input, guildId: activeGuildId },
      });

      setChannels((previous) => [...previous, channel]);
    },
    [activeGuildId],
  );

  const updateChannel = useCallback(async (channelId: string, patch: UpdateChannelInput) => {
    const { channel } = await apiFetch<{ channel: Channel }>(`/api/channels/${channelId}`, {
      method: 'PATCH',
      body: patch,
    });

    setChannels((previous) => previous.map((item) => (item.id === channel.id ? channel : item)));
  }, []);

  /**
   * The conversation being read has to move when the channel under it is
   * removed: leaving `activeChannelId` pointing at a channel that no longer
   * exists is what turns a delete into an empty screen no click recovers from.
   */
  const deleteChannel = useCallback(
    async (channelId: string) => {
      await apiFetch<void>(`/api/channels/${channelId}`, { method: 'DELETE' });

      const remaining = channels.filter((item) => item.id !== channelId);
      setChannels(remaining);
      setActiveChannelId((current) =>
        current === channelId
          ? (remaining.find((item) => item.type === 'text')?.id ?? null)
          : current,
      );
    },
    [channels],
  );

  const createCategory = useCallback(
    async (name: string) => {
      if (activeGuildId === null) {
        return;
      }

      const { category } = await apiFetch<{ category: Category }>('/api/categories', {
        method: 'POST',
        body: { name, guildId: activeGuildId },
      });

      setCategories((previous) => [...previous, category]);
    },
    [activeGuildId],
  );

  const updateCategory = useCallback(async (categoryId: string, name: string) => {
    const { category } = await apiFetch<{ category: Category }>(
      `/api/categories/${categoryId}`,
      { method: 'PATCH', body: { name } },
    );

    setCategories((previous) =>
      previous.map((item) => (item.id === category.id ? category : item)),
    );
  }, []);

  /**
   * The channels that were in the group stay: the database declares
   * `channels.category_id` as `ON DELETE SET NULL`, and the same is done here
   * so the sidebar shows them ungrouped without waiting for a reload.
   */
  const deleteCategory = useCallback(async (categoryId: string) => {
    await apiFetch<void>(`/api/categories/${categoryId}`, { method: 'DELETE' });

    setCategories((previous) => previous.filter((item) => item.id !== categoryId));
    setChannels((previous) =>
      previous.map((item) => (item.categoryId === categoryId ? { ...item, categoryId: null } : item)),
    );
  }, []);

  const reorderChannels = useCallback(
    async (order: ChannelPlacement[]) => {
      if (activeGuildId === null) {
        return;
      }

      const { channels: arranged } = await apiFetch<{ channels: Channel[] }>(
        `/api/guilds/${activeGuildId}/channels/order`,
        { method: 'PATCH', body: { channels: order } },
      );

      setChannels(arranged);
    },
    [activeGuildId],
  );

  /**
   * Moderation. Each one writes to the API first and only then to the screen,
   * like everything else here: the API is what refuses, and its refusal
   * arrives as an `ApiError` for the dialog to show.
   *
   * Dropping the person from the list on the way back is not decoration —
   * whoever pressed the button has no other sign that it worked, and a member
   * list that still shows them is indistinguishable from a request that was
   * silently refused.
   */
  const forgetMember = useCallback((userId: string) => {
    setMembers((previous) => previous.filter((member) => member.user.id !== userId));
  }, []);

  const kickMember = useCallback(
    async (userId: string) => {
      if (activeGuildId === null) {
        return;
      }

      await apiFetch<void>(`/api/guilds/${activeGuildId}/members/${userId}`, {
        method: 'DELETE',
      });
      forgetMember(userId);
    },
    [activeGuildId, forgetMember],
  );

  const banMember = useCallback(
    async (userId: string, reason?: string) => {
      if (activeGuildId === null) {
        return;
      }

      await apiFetch<{ ban: GuildBan }>(`/api/guilds/${activeGuildId}/bans`, {
        method: 'POST',
        body: reason === undefined ? { userId } : { userId, reason },
      });
      forgetMember(userId);
    },
    [activeGuildId, forgetMember],
  );

  const listBans = useCallback(async () => {
    if (activeGuildId === null) {
      return [];
    }

    const { bans } = await apiFetch<{ bans: GuildBan[] }>(`/api/guilds/${activeGuildId}/bans`);
    return bans;
  }, [activeGuildId]);

  const revokeBan = useCallback(
    async (userId: string) => {
      if (activeGuildId === null) {
        return;
      }

      await apiFetch<void>(`/api/guilds/${activeGuildId}/bans/${userId}`, { method: 'DELETE' });
    },
    [activeGuildId],
  );

  const loadOlder = useCallback(() => {
    if (activeChannelId === null || nextCursor === null || loadingOlder) {
      return;
    }

    setLoadingOlder(true);

    apiFetch<MessagePage>(
      `/api/channels/${activeChannelId}/messages?limit=${PAGE_SIZE}&before=${nextCursor}`,
    )
      .then((page) => {
        setMessages((previous) => {
          const known = new Set(previous.map((message) => message.id));
          return [...page.messages.filter((message) => !known.has(message.id)), ...previous];
        });
        setNextCursor(page.nextCursor);
      })
      .catch((cause: unknown) => {
        setError(describe(cause));
      })
      .finally(() => {
        setLoadingOlder(false);
      });
  }, [activeChannelId, nextCursor, loadingOlder]);

  const sendMessage = useCallback(
    async (content: string) => {
      if (activeChannelId === null) {
        return;
      }

      const { message } = await apiFetch<{ message: Message }>(
        `/api/channels/${activeChannelId}/messages`,
        { method: 'POST', body: { content } },
      );

      // The broadcast reaches this socket too; the upsert makes both arrivals
      // the same single message.
      setMessages((previous) => upsert(previous, message));
    },
    [activeChannelId],
  );

  /**
   * Uploads one file to the open channel.
   *
   * The API answers with the message that carries it, already broadcast to
   * everyone else; the same upsert used by `sendMessage` makes the copy that
   * comes back over the socket the same single message.
   */
  const sendAttachment = useCallback(
    async (file: File, onProgress?: (fraction: number) => void) => {
      if (activeChannelId === null) {
        return;
      }

      const { message } = await apiUpload<{ message: Message }>(
        `/api/channels/${activeChannelId}/attachments`,
        onProgress === undefined ? { file } : { file, onProgress },
      );

      setMessages((previous) => upsert(previous, message));
    },
    [activeChannelId],
  );

  const notifyTyping = useCallback(() => {
    if (socket === null || activeChannelId === null) {
      return;
    }
    socket.emit('typing:start', { channelId: activeChannelId });
  }, [socket, activeChannelId]);

  // -------------------------------------------------------------------------
  // Derived
  // -------------------------------------------------------------------------

  const activeChannel = useMemo(
    () => channels.find((channel) => channel.id === activeChannelId) ?? null,
    [channels, activeChannelId],
  );

  const guild = useMemo(
    () => guilds.find((item) => item.id === activeGuildId) ?? null,
    [guilds, activeGuildId],
  );

  /**
   * Presence only ever arrives as a change, so the signed-in user is added
   * here: this session is connected by definition and no event will say so.
   */
  const onlineUserIds = useMemo(() => {
    const next = new Set(presentUserIds);
    if (user) {
      next.add(user.id);
    }
    return next;
  }, [presentUserIds, user]);

  const value = useMemo<ChannelsContextValue>(
    () => ({
      guilds,
      guild,
      selectGuild,
      createGuild,
      channels,
      categories,
      members,
      activeChannel,
      selectChannel,
      createChannel,
      updateChannel,
      deleteChannel,
      createCategory,
      updateCategory,
      deleteCategory,
      reorderChannels,
      kickMember,
      banMember,
      listBans,
      revokeBan,
      messages,
      hasMore: nextCursor !== null,
      loading,
      loadingMessages,
      loadingOlder,
      loadOlder,
      sendMessage,
      sendAttachment,
      notifyTyping,
      typingUserIds,
      onlineUserIds,
      voiceParticipants,
      error,
    }),
    [
      guilds,
      guild,
      selectGuild,
      createGuild,
      channels,
      categories,
      members,
      activeChannel,
      selectChannel,
      createChannel,
      updateChannel,
      deleteChannel,
      createCategory,
      updateCategory,
      deleteCategory,
      reorderChannels,
      kickMember,
      banMember,
      listBans,
      revokeBan,
      messages,
      nextCursor,
      loading,
      loadingMessages,
      loadingOlder,
      loadOlder,
      sendMessage,
      sendAttachment,
      notifyTyping,
      typingUserIds,
      onlineUserIds,
      voiceParticipants,
      error,
    ],
  );

  return createElement(ChannelsContext.Provider, { value }, children);
}

export function useChannels(): ChannelsContextValue {
  const value = useContext(ChannelsContext);
  if (!value) {
    throw new Error('useChannels precisa ser chamado dentro de <ChannelsProvider>.');
  }
  return value;
}
